import type {
  EligibilityRow,
  Mobility,
  TransportOptions,
  TransportService,
} from '../../api/transport'
import { formatPesewas } from '../../lib/money'

export const SIGNS = [
  'Chest pain or difficulty breathing',
  'Unconscious, fitting or very confused',
  'Heavy bleeding or a serious injury',
  'Signs of stroke — face, arm or speech',
  'Labour, or bleeding in pregnancy',
] as const

export const MOBILITY: ReadonlyArray<{
  value: Mobility
  label: string
  sub: string
}> = [
  {
    value: 'walks_unaided',
    label: 'Walks without help',
    sub: 'Can get in and out of a car alone',
  },
  {
    value: 'walks_with_help',
    label: 'Walks with some help',
    sub: 'Needs an arm, a stick or a frame',
  },
  {
    value: 'wheelchair',
    label: 'Uses a wheelchair',
    sub: 'Travels seated in the wheelchair',
  },
  {
    value: 'stretcher',
    label: 'Needs to lie down',
    sub: 'Stretcher transfer',
  },
]

export const SERVICE_NAME: Record<TransportService, string> = {
  standard_ride: 'Standard Ride',
  pts: 'Patient Transport',
  car_hire: 'Car Hire with Driver',
  executive: 'Executive',
}

/** Why a service suits the passenger, for the recommendation line. */
export function serviceWhy(
  service: TransportService,
  mobility: Mobility,
): string {
  if (service === 'standard_ride') return 'a saloon car with a vetted driver'
  if (service !== 'pts') return SERVICE_NAME[service]
  switch (mobility) {
    case 'wheelchair':
      return 'an adapted vehicle with a ramp and a trained attendant'
    case 'stretcher':
      return 'an adapted vehicle for stretcher transfer, with a trained attendant'
    default:
      return 'an adapted vehicle with a trained attendant'
  }
}

/** Screen 2's cards, in this order, always all four (spec §C.3.2). */
export const SERVICES: ReadonlyArray<TransportService> = [
  'standard_ride',
  'pts',
  'car_hire',
  'executive',
]

export const SERVICE_DESCRIPTION: Record<TransportService, string> = {
  standard_ride: 'Saloon car with a vetted partner driver.',
  pts: 'Adapted vehicle with ramp or stretcher. Driver plus trained attendant who helps from door to ward.',
  car_hire: 'A car and driver at your disposal for several stops in one day.',
  executive: 'Premium vehicle, chauffeur and hospital meet-and-greet.',
}

/**
 * What one card says and whether it can be chosen. Every decision comes from
 * the options response (the server's matrix and service status); the page
 * decides nothing about eligibility itself.
 *
 *   `members`  Executive where the matrix allows it: booked by phone only
 *              (owner Q7), with the desk number
 *   `reason`   the matrix says no (disabled, phone_only): the server's reason.
 *              Executive too, for a passenger the matrix rules out (wheelchair,
 *              oxygen, stretcher): no invitation to phone for it
 *   `offline`  allowed, but not bookable yet (no fares or capacity)
 *   `open`     selectable; `recommended` and `caregiver` add a label or note
 */
export type Card =
  | { kind: 'members' }
  | { kind: 'reason'; reason: string }
  | { kind: 'offline' }
  | {
      kind: 'open'
      recommended: boolean
      caregiver: string | null
    }

export function serviceCard(
  service: TransportService,
  row: EligibilityRow | undefined,
  options: TransportOptions,
): Card {
  const cell = row?.services[service]
  if (service === 'executive' && cell?.state === 'phone_only') {
    return { kind: 'members' }
  }
  if (!cell || cell.state === 'disabled' || cell.state === 'phone_only') {
    return {
      kind: 'reason',
      reason: cell?.reason ?? 'Not available for this passenger.',
    }
  }
  const status = options.services.find((s) => s.service === service)
  if (!status?.bookable) return { kind: 'offline' }
  return {
    kind: 'open',
    recommended: cell.state === 'recommended',
    caregiver:
      cell.state === 'needs_caregiver'
        ? (cell.reason ??
          'Only if a family member or caregiver travels with the passenger.')
        : null,
  }
}

/** "From GHS 45.00 per leg", or Car Hire's two day rates; null without fares. */
export function startingPrice(
  service: TransportService,
  options: TransportOptions,
): string | null {
  const price = options.services.find((s) => s.service === service)?.from_price
  if (!price) return null
  if (service === 'car_hire') {
    if (price.half_day_pesewas === null || price.full_day_pesewas === null)
      return null
    return `Half day ${formatPesewas(price.half_day_pesewas)} · Full day ${formatPesewas(price.full_day_pesewas)}`
  }
  return price.per_leg_pesewas === null
    ? null
    : `From ${formatPesewas(price.per_leg_pesewas)} per leg`
}

/** "Book at least 24 hours ahead"; null when no notice is set. */
export function minimumNotice(
  service: TransportService,
  options: TransportOptions,
): string | null {
  const minutes = options.services.find(
    (s) => s.service === service,
  )?.min_notice_minutes
  if (minutes == null || minutes <= 0) return null
  const span =
    minutes % 60 === 0
      ? `${minutes / 60} ${minutes === 60 ? 'hour' : 'hours'}`
      : `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`
  return `Book at least ${span} ahead`
}
